/**
 * AppointmentDetail View (T065)
 * Full appointment details with customer link
 */

import { Calendar, Clock, User, MapPin, Video, FileText, X, ExternalLink } from 'lucide-react';
import { useAppointment } from '../../hooks/useAppointments';
import { formatDuration } from '../../hooks/useRecordings';

// Status badge per FR-026 (T067)
function AppointmentStatusBadge({ status, startTime }: { status: string; startTime: string }) {
  const now = new Date();
  const start = new Date(startTime);
  const hoursUntil = (start.getTime() - now.getTime()) / 3_600_000;

  let className = 'claude-badge ';
  let label = status;

  switch (status) {
    case 'SCHEDULED':
    case 'CONFIRMED':
      if (hoursUntil > 24) {
        className += 'claude-badge-success'; // green (>24h)
      } else {
        className += 'claude-badge-warning'; // amber (<24h)
      }
      label = status === 'CONFIRMED' ? 'Confirmed' : 'Scheduled';
      break;
    case 'IN_PROGRESS':
      className += 'claude-badge-warning';
      label = 'In Progress';
      break;
    case 'COMPLETED':
      // blue for completed
      className += 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      label = 'Completed';
      break;
    case 'CANCELLED':
      className += 'claude-badge-error'; // red
      label = 'Cancelled';
      break;
    case 'NO_SHOW':
      // gray for no-show
      className += 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
      label = 'No Show';
      break;
    default:
      className += 'claude-badge-default';
  }

  return <span className={className}>{label}</span>;
}

interface Props {
  appointmentId: string;
  onClose: () => void;
}

export default function AppointmentDetail({ appointmentId, onClose }: Props) {
  const { data: appointment, isLoading, error } = useAppointment(appointmentId);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="claude-card w-full max-w-2xl p-8">
          <div className="flex items-center justify-center">
            <div className="claude-spinner mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="claude-card w-full max-w-2xl p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Error</h2>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-destructive">Failed to load appointment details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="claude-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex items-start justify-between">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold tracking-tight">{appointment.title}</h2>
            <AppointmentStatusBadge status={appointment.status} startTime={appointment.startTime} />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Link */}
          {appointment.customer && (
            <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{appointment.customer.name}</p>
                <p className="text-sm text-muted-foreground">{appointment.customer.email}</p>
                {appointment.customer.phone && (
                  <p className="text-sm text-muted-foreground">{appointment.customer.phone}</p>
                )}
              </div>
              <a
                href={`/customers?id=${appointment.customerId}`}
                className="claude-button claude-button-secondary text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                View Profile
              </a>
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Date</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <p className="font-medium">
                  {new Date(appointment.startTime).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Time</p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <p className="font-medium">
                  {new Date(appointment.startTime).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}{' '}
                  –{' '}
                  {new Date(appointment.endTime).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Duration & Location */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Duration</p>
              <p>{appointment.duration} minutes</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Location</p>
              {appointment.meetingUrl ? (
                <a
                  href={appointment.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <Video className="w-4 h-4" />
                  Join Video Call
                </a>
              ) : appointment.location ? (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{appointment.location}</span>
                </div>
              ) : (
                <span className="text-muted-foreground">Not specified</span>
              )}
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Notes</p>
              <p className="text-sm bg-muted/30 rounded-lg p-3">{appointment.notes}</p>
            </div>
          )}

          {/* Description */}
          {appointment.description && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{appointment.description}</p>
            </div>
          )}

          {/* Cancellation Info */}
          {appointment.status === 'CANCELLED' && appointment.cancelledReason && (
            <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
              <p className="text-sm font-medium text-destructive">Cancellation Reason</p>
              <p className="text-sm mt-1">{appointment.cancelledReason}</p>
              {appointment.cancelledAt && (
                <p className="text-xs text-muted-foreground mt-2">
                  Cancelled on {new Date(appointment.cancelledAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Recording & Invoice (T121 — Enhanced) */}
          <div className="grid grid-cols-1 gap-4">
            {appointment.recording && (
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Video className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium text-sm">Session Recording</span>
                  </div>
                  <a
                    href={`/recordings`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    View Details <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  {appointment.recording.duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(appointment.recording.duration)}
                    </span>
                  )}
                </div>
              </div>
            )}
            {appointment.invoice && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">
                    Invoice {appointment.invoice.invoiceNumber}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Acuity ID */}
          {appointment.acuityId && (
            <p className="text-xs text-muted-foreground">Acuity ID: {appointment.acuityId}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export { AppointmentStatusBadge };
